/**
 * NarrowViewportNotice — advisory shown in place of the entry form when the
 * current viewport is narrower than the Landscape_Layout breakpoint (AC 8.5).
 *
 * Two exports:
 * - {@link createViewportMatcher} wraps `window.matchMedia` behind a tiny
 *   subscription surface so the rest of the app can swap in a test double
 *   without touching `window` globals.
 * - {@link renderNarrowViewportNotice} returns the detached DOM node for
 *   the "please rotate / widen" copy.
 *
 * See `.kiro/specs/marketing-roast-machine/design.md` — "Component Tree"
 * and Requirement 8.
 */

/**
 * Abstraction over `window.matchMedia` so screens can react to the
 * Landscape_Layout breakpoint without coupling to the `window` global.
 */
export interface ViewportMatcher {
  /** Returns `true` when the current viewport matches the target query. */
  isWide(): boolean;
  /**
   * Subscribe to query-match changes. The listener is invoked with the
   * new match state. Returns an unsubscribe function.
   */
  subscribe(listener: (wide: boolean) => void): () => void;
}

/**
 * Build a {@link ViewportMatcher} backed by `win.matchMedia(query)`.
 *
 * Defaults to the production values (global `window`, the 1024 px
 * breakpoint) so callers can invoke `createViewportMatcher()` with no
 * arguments.
 */
export function createViewportMatcher(
  win: Window = window,
  query: string = '(min-width: 1024px)',
): ViewportMatcher {
  const mql = win.matchMedia(query);

  return {
    isWide: () => mql.matches,
    subscribe: (listener) => {
      const handler = (event: MediaQueryListEvent): void => {
        listener(event.matches);
      };
      mql.addEventListener('change', handler);
      return () => {
        mql.removeEventListener('change', handler);
      };
    },
  };
}

/**
 * Build the detached DOM element for the narrow-viewport advisory.
 *
 * Copy is fixed at "Please use a wider screen or rotate to landscape to
 * roast your marketing." per AC 8.5.
 */
export function renderNarrowViewportNotice(): HTMLElement {
  const notice = document.createElement('div');
  notice.className = 'narrow-viewport-notice';
  notice.setAttribute('role', 'note');
  notice.textContent =
    'Please use a wider screen or rotate to landscape to roast your marketing.';
  return notice;
}
